// NewsBlogPage.jsx
// ✅ News + Blog + Event
// ✅ Tabs (All / News / Blog / Event) + Search + Pagination
// ✅ Card click -> route open (Link)

import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Stack,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import img1 from "../assets/blog1.png";
import img2 from "../assets/blog2.png";
import img3 from "../assets/blog3.png";
import img4 from "../assets/blog4.png";
import img5 from "../assets/blog5.png";
import eventImg from "../assets/IITGROUP.png"; // add your event image here

/* -------------------- DATA -------------------- */

const NEWS_POSTS = [
  {
    id: "news-1",
    type: "News",
    title: "The Problem of Fluid and Electrolyte Imbalance",
    description:
      "Fluid and electrolyte imbalance is a significant and costly challenge for healthcare systems worldwide. It impacts patient outcomes, increases healthcare expenditures, and places a substantial burden on medical resources.",
    date: "2026-01-10",
    image: img1,
    route: "/BlogPostPage1",
  },
  {
    id: "news-2",
    type: "News",
    title:
      "Most Of Hospitalized Patients May Suffer Fluid & Electrolyte Imbalance At Some Point Of There Stay",
    description:
      "Fluid and electrolyte imbalances are common in hospitalized patients—particularly in critically ill patients, older adults, and those with chronic conditions like kidney disease, heart failure, or diabetes.",
    date: "2025-12-18",
    image: img2,
    route: "/BlogPostPage2",
  },
];

const BLOG_POSTS = [
  {
    id: "blog-1",
    type: "Blog",
    title: "Morbidity And Mortality Due To Fluid & Electrolyte Imbalance",
    description:
      "Fluid and electrolyte imbalances are significant contributors to morbidity and mortality, especially in vulnerable populations such as hospitalized patients, the elderly, and those with chronic conditions.",
    date: "2026-01-05",
    image: img3,
    route: "/BlogPostPage3",
  },
  {
    id: "blog-2",
    type: "Blog",
    title: "Main Reasons To Fast Business Builder",
    description:
      "Fast business building is about strong systems, clear direction, and efficient execution—focused on structure, clarity, and repeatable processes.",
    date: "2025-10-30",
    image: img4,
    route: "/BlogPostPage4",
  },
  {
    id: "blog-3",
    type: "Blog",
    title:
      "Most Of Hospitalized Patients May Suffer Fluid & Electrolyte Imbalance At Some Point Of There Stay",
    description:
      "Estimates vary by population and setting, but imbalances are particularly frequent in critically ill patients and those with chronic comorbidities.",
    date: "2025-01-01",
    image: img5,
    route: "/BlogPostPage5",
  },
];

const EVENT_POSTS = [
  {
    id: "event-1",
    type: "Event",
    title: "SETU 2026 – FLUDEE Showcase Event",
    description:
      "Explore our participation in SETU 2026, where FLUDEE was presented through a formal presentation and live demo session.",
    date: "2026-03-27",
    image: eventImg,
    route: "/event",
  },
];

/* -------------------- HELPERS -------------------- */

const niceDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

/* -------------------- CARD -------------------- */

function CenterImageCard({ post }) {
  return (
    <Card
      sx={{
        maxWidth: 420,
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        transition: "0.3s ease",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: 6,
        },
      }}
    >
      <CardActionArea
        component={Link}
        to={post.route || "/news"}
        sx={{ height: "100%" }}
      >
        <Box
          component="img"
          src={post.image}
          alt={post.title}
          loading="lazy"
          sx={{
            width: "100%",
            height: 210,
            objectFit: "cover",
            display: "block",
            backgroundColor: "#f1f5f9",
          }}
        />

        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
          >
            {post.type} • {niceDate(post.date)}
          </Typography>

          <Typography variant="h6" fontWeight={800} gutterBottom>
            {post.title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {post.description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/* -------------------- PAGE -------------------- */

export default function NewsBlogPage() {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const pageSize = 6;

  const ALL_POSTS = useMemo(
    () => [...NEWS_POSTS, ...BLOG_POSTS, ...EVENT_POSTS],
    []
  );

  const filtered = useMemo(() => {
    let items = [...ALL_POSTS];

    if (tab === 1) items = [...NEWS_POSTS];
    if (tab === 2) items = [...BLOG_POSTS];
    if (tab === 3) items = [...EVENT_POSTS];

    if (search) {
      const q = search.toLowerCase();
      items = items.filter((i) =>
        `${i.title} ${i.description}`.toLowerCase().includes(q)
      );
    }

    return items.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [tab, search, ALL_POSTS]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => setPage(1), [tab, search]);

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#f5f7fa" }}>
      <Container maxWidth="lg">
        <Typography variant="h4" align="center" fontWeight={900} gutterBottom>
          News, Blog & Events
        </Typography>

        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ maxWidth: 760, mx: "auto", mb: 4 }}
        >
         
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="center"
          sx={{ mb: 4 }}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v)} centered>
            <Tab label="All" />
            <Tab label="News" />
            <Tab label="Blog" />
            <Tab label="Event" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", md: 340 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Grid container spacing={4} justifyContent="center">
          {paged.map((post) => (
            <Grid
              key={post.id}
              item
              xs={12}
              sm={6}
              md={5}
              lg={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <CenterImageCard post={post} />
            </Grid>
          ))}
        </Grid>

        <Stack alignItems="center" mt={5}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
          />
        </Stack>
      </Container>
    </Box>
  );
}